import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Progress } from '../ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Separator } from '../ui/separator';
import {
  Building2, Plus, Edit, Trash2, Users,
  ChevronRight, ChevronDown, MoreVertical, Eye, UserPlus,
} from 'lucide-react';
import { organigrammeAPI } from '../../services/api';
import { formatCurrencyCompact } from '../../lib/currency';
import { usePayrollUIConfig } from '../../hooks/usePayrollUIConfig';

const LEVEL_COLORS = [
  "bg-primary/10 text-primary",
  "bg-green-950/20 text-green-400",
  "bg-yellow-950/20 text-yellow-400",
  "bg-purple-950/20 text-purple-400",
  "bg-pink-950/20 text-pink-400",
  "bg-teal-950/20 text-teal-400",
  "bg-orange-950/20 text-orange-400",
  "bg-indigo-950/20 text-indigo-400",
  "bg-rose-950/20 text-rose-400",
];

const DepartementNode = ({
  departement,
  level = 0,
  expandedNodes,
  toggleExpand,
  selectedDepartement,
  setSelectedDepartement,
  draggedItem,
  handleDragStart,
  handleDragOver,
  handleDrop,
  setDepartementDialog,
  setDeleteDialog,
  employees,
  loadOrganigramme,
  toast,
}) => {
  const { t } = useTranslation();
  const { config } = usePayrollUIConfig();
  const currency = config?.currency || 'TND';
  const isExpanded = expandedNodes.has(departement.id);
  const hasChildren = departement.enfants && departement.enfants.length > 0;
  const occupation_rate = departement.max_staff > 0
    ? (departement.current_staff / departement.max_staff) * 100
    : 0;

  const sharedProps = {
    expandedNodes, toggleExpand, selectedDepartement, setSelectedDepartement,
    draggedItem, handleDragStart, handleDragOver, handleDrop,
    setDepartementDialog, setDeleteDialog, employees, loadOrganigramme, toast,
  };

  return (
    <div className="select-none">
      <div className={`relative group`} style={{ marginLeft: `${level * 24}px` }}>
        {draggedItem && draggedItem.id !== departement.id && (
          <div
            className="absolute -top-1 left-0 right-0 h-2 bg-blue-500/20 border-t-2 border-blue-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-t"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, departement, "before")}
            title={t("organigramme.tree.insertBefore", { name: departement.name })}
          />
        )}

        <div
          className={`flex items-center p-3 rounded-lg border transition-all hover:bg-accent/50 cursor-pointer ${
            selectedDepartement?.id === departement.id ? "bg-accent border-primary" : "border-border"
          } ${draggedItem?.id === departement.id ? "opacity-50 scale-95" : ""} ${
            draggedItem && draggedItem.id !== departement.id ? "ring-2 ring-blue-200 ring-opacity-50" : ""
          }`}
          draggable
          onDragStart={(e) => handleDragStart(e, departement)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, departement, "center")}
          onClick={() => setSelectedDepartement(departement)}
        >
          <div className="flex items-center w-6">
            {hasChildren && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(departement.id);
                }}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-3 flex-1">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-4 w-4 text-primary" />
            </div>

            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <span className="font-medium">{departement.name}</span>
                <Badge
                  className={`ml-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    LEVEL_COLORS[level % LEVEL_COLORS.length]
                  }`}
                >{t("organigramme.level", { n: level })}</Badge>

                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="ml-1 p-1 rounded hover:bg-accent text-muted-foreground focus:outline-none"
                      title={t("organigramme.assignManager")}
                      onClick={(e) => e.stopPropagation()}
                      type="button"
                    >
                      <UserPlus className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2">
                    <div className="font-semibold mb-2 text-sm">{t("organigramme.assignManager")}</div>
                    <ul className="max-h-48 overflow-y-auto">
                      <li>
                        <button
                          className="w-full text-left px-2 py-1 hover:bg-accent rounded text-sm"
                          onClick={async () => {
                            await organigrammeAPI.updateDepartement(departement.id, { manager_id: null });
                            toast({ title: t("organigramme.managerRemoved"), description: t("organigramme.noManagerFor", { name: departement.name }) });
                            loadOrganigramme();
                          }}
                        >
                          {t("organigramme.noManager")}
                        </button>
                      </li>
                      {employees.map((emp) => (
                        <li key={emp.id}>
                          <button
                            className="w-full text-left px-2 py-1 hover:bg-accent rounded text-sm"
                            onClick={async () => {
                              await organigrammeAPI.updateDepartement(departement.id, { manager_id: emp.id });
                              toast({
                                title: t("organigramme.managerAssigned"),
                                description: t("organigramme.managerAssignedDesc", { name: `${emp.first_name} ${emp.last_name}`, dept: departement.name }),
                              });
                              loadOrganigramme();
                            }}
                          >
                            {emp.first_name} {emp.last_name} - {emp.job_name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </PopoverContent>
                </Popover>

                {departement.code && (
                  <Badge variant="outline" className="text-xs">{departement.code}</Badge>
                )}
              </div>

              <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                <span>{t("organigramme.level", { n: departement.level })}</span>
                <span className="flex items-center space-x-1">
                  <Users className="h-3 w-3" />
                  <span>{departement.current_staff}/{departement.max_staff}</span>
                </span>
                {departement.annual_budget && (
                  <span className="flex items-center space-x-1">
                    <span className="text-xs">{formatCurrencyCompact(departement.annual_budget, currency)}</span>
                  </span>
                )}
              </div>

              <div className="mt-2">
                <Progress value={occupation_rate} className="h-1" />
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {departement.manager_name ? (
              <div className="flex items-center space-x-2">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {departement.manager_name.split(" ").map((n) => n[0]).join("")}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-muted-foreground">{departement.manager_name}</span>
              </div>
            ) : (
              <Badge variant="secondary" className="text-xs">{t("organigramme.unassigned")}</Badge>
            )}
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-48" align="end">
              <div className="space-y-1">
                <Button
                  variant="ghost" size="sm" className="w-full justify-start"
                  onClick={() => setDepartementDialog({ open: true, departement: null, mode: "create", parent: departement })}
                >
                  <Plus className="mr-2 h-4 w-4" />{t("organigramme.addSubDept")}
                </Button>
                <Button
                  variant="ghost" size="sm" className="w-full justify-start"
                  onClick={() => setDepartementDialog({ open: true, departement, mode: "edit", parent: null })}
                >
                  <Edit className="mr-2 h-4 w-4" />{t("common.edit")}
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start">
                  <Eye className="mr-2 h-4 w-4" />{t("organigramme.viewDetails")}
                </Button>
                <Separator />
                <Button
                  variant="ghost" size="sm" className="w-full justify-start text-red-400 hover:text-red-300"
                  onClick={() => setDeleteDialog({ open: true, departement })}
                >
                  <Trash2 className="mr-2 h-4 w-4" />{t("common.delete")}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {draggedItem && draggedItem.id !== departement.id && (
          <div
            className="absolute -bottom-1 left-0 right-0 h-2 bg-blue-500/20 border-b-2 border-blue-500 opacity-0 group-hover:opacity-100 transition-opacity rounded-b"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, departement, "after")}
            title={t("organigramme.tree.insertAfter", { name: departement.name })}
          />
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-2 space-y-2">
          {departement.enfants.map((enfant) => (
            <DepartementNode key={enfant.id} departement={enfant} level={level + 1} {...sharedProps} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DepartementNode;
